import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  TextField,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  VolunteerActivism as RestitutedIcon,
  Percent as PercentIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import apiService from '../services/api';
import CreationsByHourChart from '../components/dashboard/CreationsByHourChart';
import {
  DASHBOARD_PERIOD_OPTIONS,
  DashboardPeriod,
  filterObjectsByCreatedRange,
  formatDashboardPeriodLabel,
  resolveDashboardCreatedRange,
} from '../utils/dashboardPeriod';
import { buildObjectsByHour, isInDeliveryStatusForStats } from '../utils/creationsByHour';

type FoundRow = {
  id: number;
  status?: string;
  objectType?: string;
  object_type?: string;
  createdAt?: string;
  created_at?: string;
  claimedAt?: string | null;
  claimed_at?: string | null;
  updatedAt?: string | null;
  updated_at?: string | null;
};

function objectTypeName(o: FoundRow): string {
  return String(o.objectType || o.object_type || 'Autre').trim() || 'Autre';
}

const KpiCard: React.FC<{
  title: string;
  value: number | string;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
}> = ({ title, value, icon, iconBg, iconColor }) => (
  <Card
    variant="outlined"
    sx={{
      borderRadius: 2,
      boxShadow: 'none',
      height: '100%',
    }}
  >
    <CardContent sx={{ py: 2.5, px: 2.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {value}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            bgcolor: iconBg,
            color: iconColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            '& .MuiSvgIcon-root': { fontSize: 26 },
          }}
        >
          {icon}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const CategoryRow: React.FC<{
  name: string;
  total: number;
  claimed: number;
  inDelivery: number;
  pctReturned: number;
  pctInDelivery: number;
  maxTotal: number;
  foundColor: string;
  returnedColor: string;
  deliveryColor: string;
  returnedTrackBg: string;
  deliveryTrackBg: string;
}> = ({
  name,
  total,
  claimed,
  inDelivery,
  pctReturned,
  pctInDelivery,
  maxTotal,
  foundColor,
  returnedColor,
  deliveryColor,
  returnedTrackBg,
  deliveryTrackBg,
}) => {
  const greenWidth = maxTotal > 0 ? Math.min(100, (total / maxTotal) * 100) : 0;
  const blueWidth = total > 0 ? Math.min(100, (claimed / total) * 100) : 0;
  const orangeWidth = total > 0 ? Math.min(100, (inDelivery / total) * 100) : 0;

  return (
    <Box sx={{ py: 2, borderBottom: '1px solid', borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {total} objet{total > 1 ? 's' : ''} trouvé{total > 1 ? 's' : ''}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        Objets trouvés
      </Typography>
      <Box sx={{ height: 10, borderRadius: 1, bgcolor: 'grey.100', overflow: 'hidden', mb: 1 }}>
        <Box sx={{ width: `${greenWidth}%`, height: '100%', bgcolor: foundColor, borderRadius: 1 }} />
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        Objets restitués ({pctReturned}%)
      </Typography>
      <Box sx={{ height: 10, borderRadius: 1, bgcolor: returnedTrackBg, overflow: 'hidden', mb: 1 }}>
        <Box sx={{ width: `${blueWidth}%`, height: '100%', bgcolor: returnedColor, borderRadius: 1 }} />
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        Partis en livraison ({pctInDelivery}%)
      </Typography>
      <Box sx={{ height: 10, borderRadius: 1, bgcolor: deliveryTrackBg, overflow: 'hidden' }}>
        <Box sx={{ width: `${orangeWidth}%`, height: '100%', bgcolor: deliveryColor, borderRadius: 1 }} />
      </Box>
    </Box>
  );
};

const StatusBar: React.FC<{ label: string; value: number; max: number; color: string }> = ({
  label,
  value,
  max,
  color,
}) => {
  const w = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {value}
        </Typography>
      </Stack>
      <Box sx={{ height: 8, borderRadius: 1, bgcolor: 'grey.100', overflow: 'hidden' }}>
        <Box sx={{ width: `${w}%`, height: '100%', bgcolor: color, borderRadius: 1 }} />
      </Box>
    </Box>
  );
};

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const foundColor = theme.palette.success.main;
  const returnedColor = primaryColor;
  const deliveryColor = theme.palette.warning.main;
  const foundTrackBg = alpha(foundColor, 0.15);
  const returnedTrackBg = alpha(returnedColor, 0.12);
  const deliveryTrackBg = alpha(deliveryColor, 0.15);
  const [period, setPeriod] = useState<DashboardPeriod>('all');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const printRef = useRef<HTMLDivElement>(null);

  const { createdFrom, createdTo } = useMemo(
    () => resolveDashboardCreatedRange(period, customDateFrom, customDateTo),
    [period, customDateFrom, customDateTo],
  );

  const periodLabel = useMemo(
    () => formatDashboardPeriodLabel(period, customDateFrom, customDateTo),
    [period, customDateFrom, customDateTo],
  );

  const { data: rawObjects = [], isLoading, error } = useQuery({
    queryKey: ['found-objects', 'dashboard', period, createdFrom, createdTo, customDateFrom, customDateTo],
    queryFn: async () => {
      const res = await apiService.getFoundObjects({
        limit: 5000,
        ...(createdFrom ? { createdFrom } : {}),
        ...(createdTo ? { createdTo } : {}),
      });
      const list = (res.data?.data?.found_objects ?? []) as FoundRow[];
      return list;
    },
    staleTime: 0,
  });

  const filteredObjects = useMemo(
    () => filterObjectsByCreatedRange(rawObjects, { createdFrom, createdTo }),
    [rawObjects, createdFrom, createdTo],
  );

  const metrics = useMemo(() => {
    const list = filteredObjects;
    const total = list.length;
    const claimed = list.filter((o) => o.status === 'claimed').length;
    const foundStatus = list.filter((o) => o.status === 'found').length;
    const toShip = list.filter((o) => o.status === 'to_ship').length;
    const inTransit = list.filter((o) => o.status === 'in_transit').length;
    const inDelivery = list.filter((o) => isInDeliveryStatusForStats(o.status)).length;
    const returnRate = total > 0 ? Math.round((claimed / total) * 100) : 0;
    const deliveryRate = total > 0 ? Math.round((inDelivery / total) * 100) : 0;

    const catMap = new Map<string, { total: number; claimed: number; inDelivery: number }>();
    for (const o of list) {
      const name = objectTypeName(o);
      const cur = catMap.get(name) || { total: 0, claimed: 0, inDelivery: 0 };
      cur.total += 1;
      if (o.status === 'claimed') cur.claimed += 1;
      if (isInDeliveryStatusForStats(o.status)) cur.inDelivery += 1;
      catMap.set(name, cur);
    }
    const byCategory = Array.from(catMap.entries())
      .map(([name, v]) => ({
        name,
        total: v.total,
        claimed: v.claimed,
        inDelivery: v.inDelivery,
        pctReturned: v.total > 0 ? Math.round((v.claimed / v.total) * 100) : 0,
        pctInDelivery: v.total > 0 ? Math.round((v.inDelivery / v.total) * 100) : 0,
      }))
      .sort((a, b) => b.total - a.total);

    const maxCatTotal = byCategory.reduce((m, c) => Math.max(m, c.total), 0) || 1;
    const statusMax = Math.max(claimed, inTransit, toShip, inDelivery, 1);

    return {
      total,
      claimed,
      foundStatus,
      toShip,
      inTransit,
      inDelivery,
      returnRate,
      deliveryRate,
      byCategory,
      maxCatTotal,
      statusMax,
    };
  }, [filteredObjects]);

  const objectsByHour = useMemo(() => buildObjectsByHour(filteredObjects), [filteredObjects]);

  const handlePdf = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Erreur lors du chargement des données du tableau de bord.</Alert>;
  }

  return (
    <Box
      ref={printRef}
      id="dashboard-print"
      sx={{
        maxWidth: '100%',
        '@media print': {
          '& .no-print': { display: 'none' },
        },
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.35rem', sm: '1.75rem' } }}>
          Tableau de bord
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center" className="no-print" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="dashboard-period-label">Période</InputLabel>
            <Select
              labelId="dashboard-period-label"
              label="Période"
              value={period}
              onChange={(e) => setPeriod(e.target.value as DashboardPeriod)}
            >
              {DASHBOARD_PERIOD_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {period === 'custom' && (
            <>
              <TextField
                label="Du"
                type="date"
                size="small"
                value={customDateFrom}
                onChange={(e) => setCustomDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />
              <TextField
                label="Au"
                type="date"
                size="small"
                value={customDateTo}
                onChange={(e) => setCustomDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />
            </>
          )}
          <Button
            variant="outlined"
            color="primary"
            onClick={handlePdf}
            startIcon={<DescriptionIcon />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            PDF
          </Button>
        </Stack>
      </Stack>

      <Card
        variant="outlined"
        className="no-print"
        sx={{
          mb: 3,
          borderRadius: 2,
          borderColor: alpha(primaryColor, 0.35),
          bgcolor: alpha(primaryColor, 0.05),
        }}
      >
        <CardContent sx={{ py: 2, px: { xs: 2, sm: 2.5 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                Accès rapide
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Déclarez un objet trouvé et assignez-le à une boîte de stockage.
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/found-objects?create=1')}
              sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'auto' } }}
            >
              Publier un objet
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Total des annonces"
            value={metrics.total}
            icon={<DescriptionIcon />}
            iconBg={alpha(returnedColor, 0.12)}
            iconColor={returnedColor}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Objets trouvés"
            value={metrics.total}
            icon={<CheckCircleIcon />}
            iconBg={foundTrackBg}
            iconColor={foundColor}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Objets restitués"
            value={metrics.claimed}
            icon={<RestitutedIcon />}
            iconBg={alpha(foundColor, 0.12)}
            iconColor={foundColor}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Taux de restitution"
            value={`${metrics.returnRate}%`}
            icon={<PercentIcon />}
            iconBg="rgba(0, 0, 0, 0.06)"
            iconColor="text.primary"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} md={8}>
          <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 'none' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Répartition par catégorie
              </Typography>
              {metrics.byCategory.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Aucun objet sur la période sélectionnée.
                </Typography>
              ) : (
                metrics.byCategory.map((row) => (
                  <CategoryRow
                    key={row.name}
                    name={row.name}
                    total={row.total}
                    claimed={row.claimed}
                    inDelivery={row.inDelivery}
                    pctReturned={row.pctReturned}
                    pctInDelivery={row.pctInDelivery}
                    maxTotal={metrics.maxCatTotal}
                    foundColor={foundColor}
                    returnedColor={returnedColor}
                    deliveryColor={deliveryColor}
                    returnedTrackBg={returnedTrackBg}
                    deliveryTrackBg={deliveryTrackBg}
                  />
                ))
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 'none' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Détail des statuts
              </Typography>
              <Typography variant="subtitle2" sx={{ color: foundColor, fontWeight: 700, mb: 2 }}>
                Objets trouvés
              </Typography>
              <Typography variant="subtitle2" sx={{ color: returnedColor, fontWeight: 700, mb: 2, mt: 2 }}>
                Restitution
              </Typography>
              <StatusBar label="Restitués" value={metrics.claimed} max={metrics.statusMax} color={foundColor} />
              <StatusBar
                label="Partis en livraison"
                value={metrics.inDelivery}
                max={metrics.statusMax}
                color={deliveryColor}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, mb: 1.5 }}>
                dont {metrics.inTransit} en cours d&apos;envoi · {metrics.toShip} à expédier
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Sur place (statut « trouvé ») : <strong>{metrics.foundStatus}</strong>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 'none', mt: 2 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            Activité par heure
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Répartition horaire des déclarations, restitutions et envois en livraison sur la période sélectionnée.
          </Typography>
          <CreationsByHourChart data={objectsByHour} periodLabel={periodLabel} />
        </CardContent>
      </Card>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }} className="no-print">
        Données calculées sur les objets chargés (limite 5000). Filtre période appliqué sur la date de création de l&apos;annonce.
      </Typography>
    </Box>
  );
};

export default DashboardPage;
